/**
 * Scales the given dimensions down to fit within the specified maxWidth and/or maxHeight.
 * If both maxWidth and maxHeight are provided, the image is scaled down using the smallest scale factor.
 * If neither bound requires scaling, the original dimensions are returned.
 *
 * @param dimensions.width - The original width.
 * @param dimensions.height - The original height.
 * @param constraints.maxWidth - Optional maximum width constraint.
 * @param constraints.maxHeight - Optional maximum height constraint.
 * @returns An object with the new width and height.
 */
export function scaleDimensions(
  dimensions: { width: number; height: number; },
  constraints: { maxWidth?: number, maxHeight?: number, },
): { width: number; height: number } {
  const { width, height } = dimensions;
  const { maxWidth, maxHeight } = constraints;

  // Treat missing bounds as "infinite" so they don't restrict the scaling.
  const widthRatio = maxWidth !== undefined ? maxWidth / width : Infinity;
  const heightRatio = maxHeight !== undefined ? maxHeight / height : Infinity;

  // Choose the smallest ratio to ensure both constraints are met.
  const scaleFactor = Math.min(widthRatio, heightRatio);

  // Only scale down if needed (i.e., scaleFactor < 1)
  if (scaleFactor < 1) {
    return {
      width: Math.floor(width * scaleFactor),
      height: Math.floor(height * scaleFactor)
    };
  }

  // If the image is already within the bounds, return the original dimensions.
  return { width, height };
}
